'use strict';

var framerMotion = require('framer-motion');
var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var useBackwardEvent = require('../../../hooks/useBackwardEvent.js');
var Icon = require('../../Icon/src/Icon.js');
var Portal = require('../../Portal/src/Portal.js');
var _utils = require('../../Transition/src/_utils.js');
require('../../Transition/src/Fade.js');
require('../../Transition/src/ScaleFade.js');
require('../../Transition/src/SlideFade.js');
require('../../Transition/src/Collapse.js');
var Sheet_module = require('../styles/Sheet.module.less.js');
var SheetContext = require('./SheetContext.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const defaultDragElastic = { top: 0.5, bottom: 0.5 };
/**
 * @description Sheet component.
 *
 * @component
 * @example
 * ```jsx
 * <Sheet>content</Sheet>
 * ```
 */
const Sheet = React__namespace.forwardRef((props, ref) => {
    const { visible, closable, fullscreen, closeOnBackward = false, className, children, onClose, ...restProps } = props;
    const cx = reactUtils.useStyles(Sheet_module.default);
    const sheetRef = React__namespace.useRef(null);
    const isUnmountEvent = React__namespace.useRef(true);
    const referenceRef = reactUtils.useMergedRef(sheetRef, ref);
    const [dragElastic, setDragElastic] = React__namespace.useState(defaultDragElastic);
    // Auxiliary transparent layer helps to disable user interaction while animation is in progress
    const [isLayerEnabled, setIsLayerEnabled] = reactUtils.useBoolean(false);
    const sheetContextProviderValue = React__namespace.useMemo(() => ({ setDragElastic }), []);
    const handleClose = React__namespace.useCallback((event) => {
        reactUtils.preventBodyScroll();
        onClose?.(event);
        isUnmountEvent.current = false;
    }, [onClose]);
    const handleDragEnd = React__namespace.useCallback((event, { velocity }) => {
        const sheetEl = sheetRef.current;
        if (!sheetEl)
            return;
        const contentHeight = sheetEl.getBoundingClientRect().height;
        // Close if dragged over 60%
        if (velocity.y / contentHeight > 0.6) {
            handleClose(event);
        }
    }, [sheetRef, handleClose]);
    const handlePreventBodyScroll = React__namespace.useCallback(() => {
        if (visible && isUnmountEvent.current) {
            reactUtils.preventBodyScroll();
        }
    }, [visible, isUnmountEvent]);
    const sheetMotionProps = React__namespace.useMemo(() => ({
        initial: 'exit',
        exit: 'exit',
        variants: {
            enter: { y: 0, transition: { duration: 0.2, type: 'easeOut' } },
            exit: {
                y: sheetRef.current ? sheetRef.current.getBoundingClientRect().height : 1000,
                transition: { duration: 0.2, type: 'easeOut' },
            },
        },
        animate: visible ? 'enter' : 'exit',
    }), [visible, sheetRef]);
    const backdropMotionProps = React__namespace.useMemo(() => ({
        variants: {
            exit: {
                opacity: 0,
                transition: {
                    duration: 0.2,
                    delay: 0.2,
                    ease: _utils.EASINGS.easeOut,
                },
            },
            enter: {
                opacity: 0.6,
                transition: {
                    duration: 0.2,
                    delay: 0.1,
                    ease: _utils.EASINGS.easeIn,
                },
            },
        },
        animate: visible ? 'enter' : 'exit',
        initial: 'exit',
        exit: 'exit',
    }), [visible]);
    const dragProps = React__namespace.useMemo(() => ({
        drag: 'y',
        dragElastic,
        dragConstraints: { top: 0, bottom: 0 },
        dragMomentum: false,
        onDragEnd: handleDragEnd,
    }), [dragElastic, handleDragEnd]);
    const handlePointerDown = React__namespace.useCallback(() => setDragElastic(defaultDragElastic), []);
    React__namespace.useEffect(() => {
        if (visible) {
            reactUtils.preventBodyScroll(true);
            isUnmountEvent.current = true;
        }
        return handlePreventBodyScroll;
    }, [handlePreventBodyScroll, visible]);
    const { closeEventHandler } = useBackwardEvent.useBackwardEvent(closeOnBackward, !!visible, handleClose);
    return (React__namespace.createElement(Portal.Portal, null,
        isLayerEnabled && React__namespace.createElement("div", { className: cx('auxiliary-layer') }),
        React__namespace.createElement(framerMotion.AnimatePresence, { onExitComplete: closeEventHandler }, visible && (React__namespace.createElement("div", { className: cx('overlay') },
            React__namespace.createElement(framerMotion.motion.div, { ...backdropMotionProps, className: cx('backdrop'), onClick: handleClose }),
            React__namespace.createElement(framerMotion.motion.div, { "data-qa": "Sheet", ...dragProps, ...sheetMotionProps, ...restProps, ref: referenceRef, className: cx(className, 'container', { fullscreen }), onPointerDown: handlePointerDown, onAnimationStart: setIsLayerEnabled.on, onAnimationComplete: setIsLayerEnabled.off },
                closable && React__namespace.createElement(Icon.Icon, { className: cx('close'), icon: React__namespace.createElement(reactIcons.Cross, null), onClick: handleClose }),
                React__namespace.createElement(SheetContext.default.Provider, { value: sheetContextProviderValue }, children)))))));
});
Sheet.defaultProps = {
    closeOnBackward: false,
};
Sheet.displayName = 'Sheet';

exports.Sheet = Sheet;
//# sourceMappingURL=Sheet.js.map
